import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sessionsApi } from '@api/endpoints/sessions'
import { useSessionStore } from '../stores/sessionStore'
import { sessionKeys } from './useSession'
import { AddJournalNoteRequest, AddMapMarkerRequest, JournalEntryDto } from '@api/types/session'

/**
 * Hook for fetching and managing session journal
 */
export function useSessionJournal(sessionId: string | null) {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  // Fetch journal entries
  const journalQuery = useQuery({
    queryKey: sessionKeys.journal(sessionId!),
    queryFn: async () => {
      const result = await sessionsApi.getJournal(sessionId!)
      // Update store with journal entries
      store.setJournal(result)
      return result as JournalEntryDto[]
    },
    enabled: !!sessionId,
    staleTime: 10000, // 10 seconds
  })

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async (data: AddJournalNoteRequest) => {
      if (!sessionId) throw new Error('No active session')
      const result = await sessionsApi.addJournalNote(sessionId, data)
      return result
    },
    onSuccess: (result) => {
      if (result.data) {
        store.addJournalEntry(result.data)
      }
      if (sessionId) {
        queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
      }
    },
  })

  // Add map marker mutation
  const addMarkerMutation = useMutation({
    mutationFn: async (data: AddMapMarkerRequest) => {
      if (!sessionId) throw new Error('No active session')
      const result = await sessionsApi.addMapMarker(sessionId, data)
      return result
    },
    onSuccess: (result) => {
      if (result.data) {
        store.addJournalEntry(result.data)
        // Also add to revealed locations if it has lat/lng
        if (result.data.latitude && result.data.longitude) {
          store.addRevealedLocation({
            nodeId: `marker-${result.data.id}`,
            nodeLabel: result.data.note || 'User Marker',
            latitude: result.data.latitude,
            longitude: result.data.longitude,
            description: result.data.note,
            markerEmoji: result.data.markerEmoji || '📍',
            revealedAt: result.data.createdAt,
          })
        }
      }
      if (sessionId) {
        queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
      }
    },
  })

  // Convenience methods
  const addNote = (note: string) => {
    addNoteMutation.mutate({ note })
  }

  const addMapMarker = (
    latitude: number,
    longitude: number,
    note?: string,
    markerEmoji?: string
  ) => {
    addMarkerMutation.mutate({ latitude, longitude, note, markerEmoji })
  }

  return {
    // Journal data
    journal: journalQuery.data || store.journal,
    isLoading: journalQuery.isLoading,
    error: journalQuery.error,
    refetch: journalQuery.refetch,

    // Add note
    addNote,
    isAddingNote: addNoteMutation.isPending,
    addNoteError: addNoteMutation.error,

    // Add marker
    addMapMarker,
    isAddingMarker: addMarkerMutation.isPending,
    addMarkerError: addMarkerMutation.error,
  }
}
